import React, { useEffect, useRef } from 'react';

/**
 * "You'll be signed out in M:SS due to inactivity" (v3.56.0).
 *
 * Accessibility contract:
 *  - role="alertdialog" + aria-modal, labelled and described by its own copy.
 *  - Focus moves to "Stay signed in" on open and is TRAPPED (Tab/Shift+Tab
 *    cycle inside), then restored to whatever was focused before.
 *  - Escape does NOT dismiss. Dismissing silently would leave a teacher
 *    believing they are still signed in while the clock keeps running; the
 *    only ways out are the explicit button or expiry. Escape nudges focus
 *    back to the button so keyboard users are not left stranded.
 *  - The countdown is announced politely (aria-live) rather than assertively,
 *    so screen readers are not interrupted every second.
 *
 * Presentation matches the app's existing modal overlays (StudentDetailsModal).
 */

/** ms → "M:SS", floor-free so 119.6s reads 2:00 not 1:59. */
export function formatCountdown(remainingMs) {
  const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

export default function IdleWarningModal({ remainingMs, onStaySignedIn, onSignOutNow }) {
  const cardRef = useRef(null);
  const primaryRef = useRef(null);
  const previouslyFocusedRef = useRef(null);

  useEffect(() => {
    previouslyFocusedRef.current = document.activeElement;
    primaryRef.current?.focus();
    const restoreTo = previouslyFocusedRef.current;
    return () => {
      // Restore focus only if the element is still in the document.
      if (restoreTo && document.contains(restoreTo)) {
        try { restoreTo.focus(); } catch { /* non-fatal */ }
      }
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        // Deliberately not a dismissal — see the contract above.
        event.preventDefault();
        event.stopPropagation();
        primaryRef.current?.focus();
        return;
      }
      if (event.key !== 'Tab') return;
      const nodes = cardRef.current?.querySelectorAll(FOCUSABLE);
      if (!nodes || nodes.length === 0) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    // Capture phase: the app's global keyboard handlers (AssessmentForm
    // scores letters on keypress) must not see keys typed at this modal.
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, []);

  const countdown = formatCountdown(remainingMs);

  return (
    <div
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center',
        justifyContent: 'center', zIndex: 10000, padding: '16px',
      }}
    >
      <div
        ref={cardRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="idle-warning-title"
        aria-describedby="idle-warning-body"
        style={{
          background: 'white', padding: '32px', borderRadius: '16px',
          maxWidth: '440px', width: '90%',
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
        }}
      >
        <h3 id="idle-warning-title" style={{ marginTop: 0, color: '#0f172a' }}>
          Still there?
        </h3>
        <p id="idle-warning-body" style={{ color: '#475569', fontSize: '0.95em', lineHeight: 1.6 }}>
          You&apos;ll be signed out in{' '}
          <strong aria-live="polite" data-testid="idle-countdown">{countdown}</strong>{' '}
          due to inactivity. This keeps student data safe on shared devices.
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
          <button
            type="button"
            onClick={onSignOutNow}
            style={{
              padding: '10px 16px', background: 'white', border: '1px solid #cbd5e1',
              borderRadius: '8px', color: '#475569', cursor: 'pointer', minHeight: '44px',
            }}
          >
            Sign out now
          </button>
          <button
            type="button"
            ref={primaryRef}
            onClick={onStaySignedIn}
            style={{
              padding: '10px 16px', background: '#3b82f6', color: 'white', border: 'none',
              borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', minHeight: '44px',
            }}
          >
            Stay signed in
          </button>
        </div>
      </div>
    </div>
  );
}
